import React, { useEffect, useState, useRef } from "react";
import { Table, TableContainer, Paper, Button, IconButton } from "@mui/material";
import { formatDateToYMDSimple, getAddressCity, getNameAndCompanyName } from "./common";
import { useDataProvider, useNotify, useRedirect } from "react-admin";
import { Link } from "react-router-dom";
import SaveIcon from "@mui/icons-material/Save"; // 💾 保存アイコンをインポート
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import authProvider from "./authProvider";

// 月の日数を取得
const getDaysInMonth = (year, month) => new Date(year, month, 0).getDate();

// 曜日を取得
const getDayOfWeek = (year, month, day) => {
  const days = ["日", "月", "火", "水", "木", "金", "土"];
  return days[new Date(year, month - 1, day).getDay()];
};


const CleanReserve = () => {
  const notify = useNotify();
  const redirect = useRedirect();
  const printRef = useRef();
  // 現在の年月を管理
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [events, setEvents] = useState([]);
  const dataProvider = useDataProvider();

  const [isACollapsed, setIsACollapsed] = useState(false); // A地区の折りたたみ状態
  const [isBCollapsed, setIsBCollapsed] = useState(false); // B地区の折りたたみ状態

  const [holidays, setHolidays] = useState(new Set()); // APIから取得する祝日データ

  const [notes, setNotes] = useState([]);

  const handlePrint = () => {
    handleGeneratePdf();
  };

  const handleGeneratePdf = async () => {
    const element = document.querySelector("#pdf-target");
    const cloned = element.cloneNode(true);

    // 🔸 textarea → div 置換（PDFでの見た目のため）
    cloned.querySelectorAll("textarea").forEach(textarea => {
      const div = document.createElement("div");
      div.textContent = textarea.value;
      div.style.fontSize = textarea.style.fontSize || "12px";
      div.style.whiteSpace = "pre-wrap";
      textarea.parentNode.replaceChild(div, textarea);
    });

    // 🔸 <a>タグをすべて中身のみに置換（アンリンク化）
    cloned.querySelectorAll("a").forEach(anchor => {
      const span = document.createElement("span");
      span.innerHTML = anchor.innerHTML; // 中のテキストやタグを保持
      anchor.replaceWith(span); // <a> → <span>（リンク除去）
    });

    // 🔸 HTMLとして文字列化
    const html = cloned.outerHTML;

    const response = await fetch(`${process.env.REACT_APP_API_DIR}/downloadPDF`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ html }),
    });

    if (!response.ok) {
      alert("PDF生成に失敗しました");
      return;
    }
    console.log(response);
    if (response.error) {
      alert(response.message)
    }


    const contentType = response.headers.get("Content-Type");

    if (!response.ok || !contentType || !contentType.includes("application/pdf")) {
      const text = await response.text(); // blobでなくtextで安全に中身を見る
      console.error("PDFではない応答:", text);
      alert("PDF生成に失敗しました");
      return;
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `清掃予定表_${year}_${month}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };


  const fetchNotes = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_DIR}/cleanReserveNote/month`, {
        method: "POST",
        headers: new Headers({ "Content-Type": "application/json" }),
        mode: "cors",
        credentials: "include",
        body: JSON.stringify({ year: year, month: month }),
      });
      if (!response.ok) {
        throw new Error("Failed to fetch user info");
      }
      const { data } = await response.json();
      const updatedArray = data.map(item => (
        { ...item, date: formatDateToYMDSimple(new Date(item.date)) }
      ));
      console.log(updatedArray)
      setNotes(updatedArray);
    } catch (error) {
    } finally {
    }
  }

  // 🔹 祝日を取得する関数（外部API）
  const fetchHolidays = async () => {
    try {
      const response = await fetch("https://holidays-jp.github.io/api/v1/date.json");
      const data = await response.json();
      // APIの祝日データを `{ "YYYY-MM-DD": "祝日名" }` の形式で取得 → `Set` に変換
      return new Set(Object.keys(data).map(date => {
        const [year, month, day] = date.split("-").map(Number);
        return `${month}-${day}`; // "MM-DD" 形式に変換
      }));
    } catch (error) {
      console.error("祝日データの取得に失敗:", error);
      return new Set();
    }
  };

  const isHoliday = (year, month, day) => holidays.has(`${month}-${day}`);

  // スケジュールを動的に生成
  const generateSchedule = (year, month) => {
    const daysInMonth = getDaysInMonth(year, month);
    return Array.from({ length: daysInMonth }, (_, i) => {
      const date = i + 1;
      const day = getDayOfWeek(year, month, date);
      const isHolidayToday = isHoliday(year, month, date);
      return { date, day, isHoliday: isHolidayToday };
    });
  };

  useEffect(() => {
    fetchHolidays().then(setHolidays);
    fetchData(year, month);
    fetchNotes(year, month)
  }, [dataProvider, year, month]);

  useEffect(() => {
    if (authProvider) {
      authProvider
        .checkAuth()
        .then(() => { })
        .catch(() => {
          redirect("/login")
        });
    }
  }, [authProvider]);


  const fetchData = async (year, month) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_DIR}/cleanReceptHistory/month`, {
        method: "POST",
        headers: new Headers({ "Content-Type": "application/json" }),
        mode: "cors",
        credentials: "include",
        body: JSON.stringify({ year: year, month: month }),
      });
      if (!response.ok) {
        throw new Error("Failed to fetch user info");
      }
      const { data } = await response.json();

      setEvents(
        data
          .filter(event => event.exCleaningDate) // exCleaningDate があるものだけを残す
          .map(event => ({
            id: event.receptId,
            date: (new Date(event.exCleaningDate)).getDate(),
            district: event.district,
            customerId: event.customerId,
            exPumpingAmount: event.exPumpingAmount,
            name: `${event.bukken}`,
            address: event.address3,
            cleaningTime: event.exCleaningTime,
          }))
      );
    } catch (error) {
      console.error("ファイルの読み込みに失敗しました:", error);
    } finally {
      // setLoading(false);
    }
  };

  // 月の変更
  const handleMonthChange = (direction) => {
    let newMonth = month + direction;
    let newYear = year;

    if (newMonth === 0) {
      newMonth = 12;
      newYear -= 1;
    } else if (newMonth === 13) {
      newMonth = 1;
      newYear += 1;
    }

    setYear(newYear);
    setMonth(newMonth);
  };

  // 現在のスケジュールを動的に生成
  const currentSchedule = generateSchedule(year, month);

  const handleSave = () => {
    const updateNotes = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_DIR}/cleanReserveNote`, {
          method: "POST",
          headers: new Headers({ "Content-Type": "application/json" }),
          mode: "cors",
          credentials: "include",
          body: JSON.stringify({ notes: notes }),
        });
        if (!response.ok) {
          throw new Error("Failed to fetch user info");
        }
        const { data, isError, message } = await response.json();
        if (isError) {
          notify(message, { type: 'error' });
        }
        else {
          notify('保存が完了しました', { type: 'success' });
        }
      } catch (error) {
        notify('エラーが発生しました', { type: 'error' });
      } finally {
      }
    }
    updateNotes();
  };


  const handleSetText = (date, district, value) => {

    setNotes((prevNotes) => {
      // 既存のメモがあるかチェック
      const updateDate = formatDateToYMDSimple(new Date(`${year}-${month}-${date}`));
      const existingIndex = prevNotes.findIndex(
        (note) => note.date === updateDate && note.district === district
      );

      let updatedNotes;

      let newNote = {
        date: updateDate,
        district,
        content: value
      }
      if (existingIndex !== -1) {
        // 既存のメモを更新
        updatedNotes = [...prevNotes];
        updatedNotes[existingIndex] = newNote;
      } else {
        // 新規メモを追加
        updatedNotes = [...prevNotes, newNote];
      }

      return updatedNotes;
    });
  };

  return (
    <div ref={printRef} style={{ width: "100%", textAlign: "center", padding: "20px", position: "relative" }} >
      <div className="no-print nav-btn">
        <IconButton
          color="secondary"
          onClick={handlePrint}
          aria-label="PDFで出力"
        >
          <PictureAsPdfIcon />
        </IconButton>
        <IconButton
          color="primary"
          onClick={handleSave}
        >
          <SaveIcon fontSize="large" />
        </IconButton>
      </div>
      {/* 月切り替えボタン */}
      <div style={{ display: "flex", justifyContent: "center" }}>
        <Button onClick={() => handleMonthChange(-1)} style={{ marginRight: "10px" }} className="no-print">
          ◀前月
        </Button>
        <h2>{`${year}年 ${month}月`}</h2>
        <Button onClick={() => handleMonthChange(1)} style={{ marginLeft: "10px" }} className="no-print">
          次月▶
        </Button>
      </div>

      <TableContainer component={Paper} style={{ width: "100%", margin: "auto", overflowX: "auto" }}>
        <div id="pdf-target">
          <div>
            <div className="view-print">{`${year}年`}</div>
            <div style={{ display: "flex", justifyContent: "center", alignItems: "flex-start", gap: "10px" }}>
              <div style={{ flex: isACollapsed ? "0.2" : isBCollapsed ? "2" : "1", transition: "flex 0.3s ease" }}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => setIsACollapsed(!isACollapsed)}
                  style={{ width: "100%", marginBottom: "5px" }}
                  className="no-print"
                >
                  {isACollapsed ? "▶ A地区を表示" : "◀ A地区を折りたたむ"}
                </Button>
                {!isACollapsed && (
                  <Table style={{ border: "1px solid #ccc" }} className="table-harf">
                    <thead>
                      <tr>
                        <td colSpan={7} className="pdf-margin">A地区　小室(3874)</td>
                      </tr>
                      <tr>
                        <td colSpan={2} style={{ borderRight: "1px solid #ccc", fontSize: "70%" }} className="pdf-margin">{month}月</td>
                        <td colSpan={4} style={{ borderRight: "1px solid #ccc", fontSize: "70%" }}>浄化槽清掃</td>
                        <td style={{ fontSize: "70%" }}>仮設・他</td>
                      </tr>
                    </thead>
                    <tbody>
                      {currentSchedule.map((entry, i) => {
                        // 土曜を青、日曜と祝日を赤にする
                        let textColor = "black";
                        if (entry.day === "土") textColor = "#2196F3"; // 青
                        if (entry.day === "日" || entry.isHoliday) textColor = "#D32F2F"; // 赤

                        const filteredEvents = events.filter(event => (event.date === entry.date) && (event.district === "A地区"));
                        const oneDayEvent = filteredEvents.find(event => event.cleaningTime === 5);
                        return (
                          <tr>
                            <td style={{ borderRight: "1px solid #ccc", fontSize: "70%" }}>{entry.date}</td>
                            <td style={{ color: textColor, borderRight: "1px solid #ccc", fontSize: "70%" }}>{entry.day}</td>
                            {oneDayEvent ? (
                              <td colSpan={4} className="td-content">
                                <div style={{ boxSizing: "border-box", display: "flex", justifyContent: "space-around", verticalAlign: "middle" }}>
                                  {oneDayEvent ? <div style={{ writingMode: "vertical-rl" }}>{oneDayEvent.address}</div> : ""}
                                  {oneDayEvent ? <div style={{ margin: "0 2px", borderLeft: "2px #ccc dotted" }} /> : ""}
                                  {oneDayEvent ? <div style={{ margin: "auto 0" }}><Link to={`/customers-clean/show/${oneDayEvent.customerId}`}>{oneDayEvent.name}</Link></div> : ""}
                                  {oneDayEvent ? <div style={{ margin: "0 2px", borderLeft: "2px #ccc dotted" }} /> : ""}
                                  {oneDayEvent ? <div style={{ fontSize: "90%", margin: "auto 0" }}>{oneDayEvent.exPumpingAmount}</div> : ""}
                                </div>
                              </td>
                            ) : (
                              <>
                                {[1, 2, 3, 4].map(time => {
                                  const matchingEvent = filteredEvents.find(event => event.cleaningTime === time);
                                  return (
                                    <td className="td-content">
                                      <div style={{ boxSizing: "border-box", display: "flex", justifyContent: "space-around", verticalAlign: "middle" }} >
                                        {matchingEvent ? <div style={{ writingMode: "vertical-rl" }}>{matchingEvent.address}</div> : ""}
                                        {matchingEvent ? <div style={{ margin: "0 2px", borderLeft: "2px #ccc dotted" }} /> : ""}
                                        {matchingEvent ? <div style={{ margin: "auto 0" }}><Link to={`/customers-clean/show/${matchingEvent.customerId}`}>{matchingEvent.name}</Link></div> : ""}
                                        {matchingEvent ? <div style={{ margin: "0 2px", borderLeft: "2px #ccc dotted" }} /> : ""}
                                        {matchingEvent ? <div style={{ fontSize: "90%", margin: "auto 0" }}>{matchingEvent.exPumpingAmount}</div> : ""}
                                      </div>
                                    </td>
                                  );
                                })}
                              </>
                            )}

                            <td style={{ margin: 0, padding: 0 }} className="other-content">
                              <textarea
                                value={notes && notes.find(
                                  (item) => item.date === formatDateToYMDSimple(new Date(`${year}-${month}-${entry.date}`)) && item.district === 0
                                )?.content || ""
                                }
                                onChange={(e) => handleSetText(entry.date, 0, e.target.value)}
                                style={{
                                  fontSize: "70%",
                                  width: "100%",
                                  height: "100%",
                                  resize: "none",         // ユーザーのリサイズを防ぐ（必要に応じて）
                                  boxSizing: "border-box", // padding を含めてサイズを計算
                                  border: "none",         // デフォルトの枠を消す（見た目合わせたいとき）
                                }}
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                )}
              </div>
              <div style={{ flex: isBCollapsed ? "0.2" : isACollapsed ? "2" : "1", transition: "flex 0.3s ease" }}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => setIsBCollapsed(!isBCollapsed)}
                  style={{ width: "100%", marginBottom: "5px" }}
                  className="no-print"
                >
                  {isBCollapsed ? "◀ B地区を表示" : "▶ B地区を折りたたむ"}
                </Button>
                {!isBCollapsed && (
                  <Table style={{ border: "1px solid #ccc" }} className="table-harf">
                    <thead>
                      <tr>
                        <td colSpan={7} className="pdf-margin">B地区　平山(5054)</td>
                      </tr>
                      <tr>
                        <td colSpan={2} style={{ borderRight: "1px solid #ccc", fontSize: "70%" }} className="pdf-margin">{month}月</td>
                        <td colSpan={4} style={{ borderRight: "1px solid #ccc", fontSize: "70%" }}>浄化槽清掃</td>
                        <td style={{ fontSize: "70%" }}>仮設・他</td>
                      </tr>
                    </thead>
                    <tbody>
                      {currentSchedule.map((entry, i) => {
                        // 土曜を青、日曜と祝日を赤にする
                        let textColor = "black";
                        if (entry.day === "土") textColor = "#2196F3"; // 青
                        if (entry.day === "日" || entry.isHoliday) textColor = "#D32F2F"; // 赤

                        const filteredEvents = events.filter(event => (event.date === entry.date) && (event.district === "B地区"));
                        const oneDayEvent = filteredEvents.find(event => event.cleaningTime === 5);
                        return (
                          <tr key={i}>
                            <td style={{ borderRight: "1px solid #ccc", fontSize: "70%" }}>{entry.date}</td>
                            <td style={{ color: textColor, borderRight: "1px solid #ccc", fontSize: "70%" }}>{entry.day}</td>
                            {oneDayEvent ? (
                              <td colSpan={4} className="td-content">
                                <div style={{ boxSizing: "border-box", display: "flex", justifyContent: "space-around", verticalAlign: "middle" }}>
                                  {oneDayEvent ? <div style={{ writingMode: "vertical-rl" }}>{oneDayEvent.address}</div> : ""}
                                  {oneDayEvent ? <div style={{ margin: "0 2px", borderLeft: "2px #ccc dotted" }} /> : ""}
                                  {oneDayEvent ? <div style={{ margin: "auto 0" }}><Link to={`/customers-clean/show/${oneDayEvent.customerId}`}>{oneDayEvent.name}</Link></div> : ""}
                                  {oneDayEvent ? <div style={{ margin: "0 2px", borderLeft: "2px #ccc dotted" }} /> : ""}
                                  {oneDayEvent ? <div style={{ fontSize: "90%", margin: "auto 0" }}>{oneDayEvent.exPumpingAmount}</div> : ""}
                                </div>
                              </td>
                            ) : (
                              <>
                                {[1, 2, 3, 4].map(time => {
                                  const matchingEvent = filteredEvents.find(event => event.cleaningTime === time);
                                  return (
                                    <td className="td-content">
                                      <div style={{ boxSizing: "border-box", display: "flex", justifyContent: "space-around", verticalAlign: "middle" }} >
                                        {matchingEvent ? <div style={{ writingMode: "vertical-rl" }}>{matchingEvent.address}</div> : ""}
                                        {matchingEvent ? <div style={{ margin: "0 2px", borderLeft: "2px #ccc dotted" }} /> : ""}
                                        {matchingEvent ? <div style={{ margin: "auto 0" }}><Link to={`/customers-clean/show/${matchingEvent.customerId}`}>{matchingEvent.name}</Link></div> : ""}
                                        {matchingEvent ? <div style={{ margin: "0 2px", borderLeft: "2px #ccc dotted" }} /> : ""}
                                        {matchingEvent ? <div style={{ fontSize: "90%", margin: "auto 0" }}>{matchingEvent.exPumpingAmount}</div> : ""}
                                      </div>
                                    </td>
                                  );
                                })}
                              </>
                            )}
                            <td style={{ margin: 0, padding: 0 }} className="other-content">
                              <textarea
                                value={notes && notes.find(
                                  (item) => item.date === formatDateToYMDSimple(new Date(`${year}-${month}-${entry.date}`)) && item.district === 1
                                )?.content || ""
                                }
                                onChange={(e) => handleSetText(entry.date, 1, e.target.value)}
                                style={{
                                  fontSize: "70%",
                                  width: "100%",
                                  height: "100%",
                                  resize: "none",         // ユーザーのリサイズを防ぐ（必要に応じて）
                                  boxSizing: "border-box", // padding を含めてサイズを計算
                                  border: "none",         // デフォルトの枠を消す（見た目合わせたいとき）
                                }}

                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                )}
              </div>
            </div>
          </div>
        </div>

      </TableContainer>
    </div>
  );
};

export default CleanReserve;

